"use client";

import { useQuery } from "@tanstack/react-query";

import type { SetupReadiness } from "@/types/clinic-setup";

import { fetchClinicSetupReadiness } from "@/api/clinic-setup";
import { setupReadinessQueryKeys } from "@/query/clinic-setup-readiness";

/** Onboarding readiness for the current tenant. */
export function useSetupReadiness() {
  return useQuery({
    queryKey: setupReadinessQueryKeys.all,
    queryFn: () => fetchClinicSetupReadiness(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/** Whether this clinic is being onboarded at all — not whether go-live is still */
export function isInOnboardingWindow(
  readiness: SetupReadiness | null | undefined,
): boolean {
  if (!readiness || readiness.progress.isLegacyTenant) return false;
  if (readiness.progress.onboardingWindowClosed) return false;
  return readiness.progress.daysToGoLive !== null;
}

/** Past go-live, an unfinished checklist stops being a countdown and becomes a problem. */
export function isPastGoLive(
  readiness: SetupReadiness | null | undefined,
): boolean {
  const days = readiness?.progress.daysToGoLive;
  return days !== null && days !== undefined && days < 0;
}

/** The hook form, for components that don't already hold readiness. */
export function useInOnboardingWindow(): boolean {
  return isInOnboardingWindow(useSetupReadiness().data);
}
